import React from "react";
import { useState, useEffect } from "react";
import { Tabs, Button, Row, Col, Popconfirm, Spin, Space } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { deleteClient, getClientById } from "@/ts/retrieve-data";
import { showToast } from "@/ts/modals";
import { IClientListItem, ParentModule, ClientTabs } from "@/ts/common-types";
import Layout from "@/components/wrappers/layouts/main-layout/Layout";
import ClientIntegrations from "./ClientIntegrations";
import ClientMembers from "./ClientMembers";
import ClientForm from "./ClientForm";
import ClientJobs from "./ClientJobs";
import { LeftOutlined } from "@ant-design/icons";
import { usePermissionCheck } from "@/hooks/AllCustomHooks";
import ClientBrands from "./ClientBrands";
import ClientDetails from "./ClientDetails";
import "@/scss/admin-view.scss";

const { TabPane } = Tabs;

const tabContentStyle: React.CSSProperties = {
    height: "calc(100vh - 300px)",
    overflowY: "auto",
    overflowX: "hidden",
    padding: "20px 0",
    cursor: "default",
};

const ClientView = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<ClientTabs>(ClientTabs.DETAILS);
    const [loading, setLoading] = useState(true);
    const [clientData, setClientData] = useState<IClientListItem | null>(null);
    const [tabLoading, setTabLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const { canUpdateClient, canDeleteClient } = usePermissionCheck();

    useEffect(() => {
        const fetchClientData = async () => {
            try {
                const client = await getClientById(Number(clientId));

                if (!client) {
                    showToast("danger", "Client not found");
                    navigate(
                        `/${ParentModule.ADMIN_SETTINGS}/settings/clients`
                    );
                    return;
                }

                setClientData(client);
            } catch (error: any) {
                showToast("danger", error.message || "Failed to load client");
                navigate(`/${ParentModule.ADMIN_SETTINGS}/settings/clients`);
            } finally {
                setLoading(false);
            }
        };

        if (clientId) {
            fetchClientData();
        }
    }, [clientId, navigate]);

    const handleDelete = async () => {
        try {
            await deleteClient(Number(clientId));
            showToast("success", "Client deleted successfully");
            navigate(`/${ParentModule.ADMIN_SETTINGS}/settings/clients`);
        } catch (error: any) {
            showToast("danger", error.message || "Failed to delete client");
        }
    };

    const handleEditSuccess = async () => {
        setIsEditing(false);
        setTabLoading(true);
        try {
            const client = await getClientById(Number(clientId));
            setClientData(client);
            showToast("success", "Client updated successfully");
        } catch (error) {
            showToast("danger", "Failed to refresh client data");
        } finally {
            setTabLoading(false);
        }
    };

    if (loading)
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "100vh" }}
            >
                <Spin size="large" />
            </div>
        );
    if (!clientData) return null;

    return (
        <Layout>
            <div className="client-view-container">
                {!isEditing ? (
                    <div className="card shadow">
                        <div className="p-4" style={{ cursor: "default" }}>
                            <Row
                                className="mb-4"
                                justify="space-between"
                                align="middle"
                            >
                                <Col>
                                    <Space size="middle">
                                        <Button
                                            onClick={() =>
                                                navigate(
                                                    `/${ParentModule.ADMIN_SETTINGS}/settings/clients`
                                                )
                                            }
                                            type="default"
                                            className="d-flex align-items-center"
                                            style={{ gap: "4px" }}
                                        >
                                            <LeftOutlined /> Back to Clients
                                        </Button>
                                    </Space>
                                </Col>
                                <Col>
                                    <Space>
                                        {canUpdateClient && (
                                            <Button
                                                onClick={() =>
                                                    setIsEditing(true)
                                                }
                                                type="primary"
                                            >
                                                Edit
                                            </Button>
                                        )}
                                        {canDeleteClient && (
                                            <Popconfirm
                                                title="Delete Client"
                                                description="Are you sure you want to delete this client?"
                                                onConfirm={handleDelete}
                                                okText="Yes"
                                                cancelText="No"
                                                okButtonProps={{
                                                    danger: true,
                                                    className: "ms-2",
                                                }}
                                                overlayClassName="p-3"
                                                styles={{
                                                    body: { padding: "12px" },
                                                }}
                                            >
                                                <Button danger>Delete</Button>
                                            </Popconfirm>
                                        )}
                                    </Space>
                                </Col>
                            </Row>

                            <h2 className="mb-4">
                                {clientData.id} - {clientData.name}
                            </h2>

                            <Tabs
                                activeKey={activeTab}
                                onChange={(key) =>
                                    setActiveTab(key as ClientTabs)
                                }
                                className="client-view-tabs"
                                size="large"
                            >
                                <TabPane tab="Details" key={ClientTabs.DETAILS}>
                                    <div style={tabContentStyle}>
                                        {tabLoading ? (
                                            <div className="text-center">
                                                <Spin />
                                            </div>
                                        ) : (
                                            <ClientDetails
                                                client={clientData}
                                            />
                                        )}
                                    </div>
                                </TabPane>
                                <TabPane tab="Brands" key={ClientTabs.BRANDS}>
                                    <div style={tabContentStyle}>
                                        {clientData && (
                                            <ClientBrands
                                                clientId={clientData.id}
                                                timezone={
                                                    clientData.timezones?.[0]
                                                }
                                            />
                                        )}
                                    </div>
                                </TabPane>
                                <TabPane
                                    tab="Integrations"
                                    key={ClientTabs.INTEGRATIONS}
                                >
                                    <div style={tabContentStyle}>
                                        <ClientIntegrations
                                            client={clientData}
                                        />
                                    </div>
                                </TabPane>
                                <TabPane tab="Members" key={ClientTabs.MEMBERS}>
                                    <div style={tabContentStyle}>
                                        {clientData && (
                                            <ClientMembers
                                                clientId={clientData.id}
                                                clientTimezone={
                                                    clientData.timezones?.[0]
                                                }
                                            />
                                        )}
                                    </div>
                                </TabPane>
                                <TabPane tab="Jobs" key={ClientTabs.JOBS}>
                                    <div style={tabContentStyle}>
                                        {clientData && (
                                            <ClientJobs client={clientData} />
                                        )}
                                    </div>
                                </TabPane>
                            </Tabs>
                        </div>
                    </div>
                ) : (
                    <div className="card shadow">
                        <div className="p-4">
                            <Row className="mb-4">
                                <Col>
                                    <h2>Edit Client: {clientData.name}</h2>
                                </Col>
                            </Row>
                            <ClientForm
                                editData={clientData}
                                onSuccess={handleEditSuccess}
                                onCancel={() => setIsEditing(false)}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default ClientView;
